import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';
import ProfileHeader from '../components/ProfileHeader';
import { createContentPack, grantOrganizationEntitlement } from '../utils/organizationAssignments';
import {
  normalizeContentPackRecord,
  normalizeEntitlementRecord,
} from '../utils/contentPackAccess';
import { canManageContentPack } from '../utils/platformAccess';
import {
  getContentPackEntitledOrganizations,
  getOrganizationEntitlements,
  revokeOrganizationEntitlement,
  updateEntitlementDates,
} from '../utils/entitlementManagement';

function PlatformAdminContentPacksPage() {
  const { userData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [contentPacks, setContentPacks] = useState([]);
  const [entitlements, setEntitlements] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [packForm, setPackForm] = useState({
    name: '',
    description: '',
    industry: '',
    disciplinesText: '',
    isPremium: true,
  });
  const [selectedPackId, setSelectedPackId] = useState('');
  const [packEditForm, setPackEditForm] = useState({
    name: '',
    description: '',
    industry: '',
    disciplinesText: '',
    isPremium: false,
  });
  const [entitlementForm, setEntitlementForm] = useState({
    contentPackId: '',
    organizationId: '',
  });
  const [entitlementDrafts, setEntitlementDrafts] = useState({});

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [packSnap, entitlementSnap, templateSnap, organizationSnap] = await Promise.all([
          getDocs(collection(db, 'contentPacks')),
          getDocs(collection(db, 'entitlements')),
          getDocs(collection(db, 'drillTemplates')),
          getDocs(collection(db, 'organizations')),
        ]);

        setContentPacks(
          packSnap.docs.map((docSnap) =>
            normalizeContentPackRecord({ id: docSnap.id, ...docSnap.data() })
          )
        );
        setEntitlements(
          entitlementSnap.docs.map((docSnap) =>
            normalizeEntitlementRecord({ id: docSnap.id, ...docSnap.data() })
          )
        );
        setTemplates(
          templateSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        );
        setOrganizations(
          organizationSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        );
      } catch (loadError) {
        console.error(loadError);
        setError('Could not load platform content packs right now.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const editable = canManageContentPack(userData);
  const packAttachmentCounts = useMemo(
    () =>
      contentPacks.reduce((acc, contentPack) => {
        acc[contentPack.contentPackId] = templates.filter(
          (template) => template.contentPackId === contentPack.contentPackId
        ).length;
        return acc;
      }, {}),
    [contentPacks, templates]
  );
  const organizationNamesById = useMemo(
    () =>
      organizations.reduce((acc, organization) => {
        acc[organization.id] = organization.name || organization.id;
        return acc;
      }, {}),
    [organizations]
  );
  const organizationEntitlementSummary = useMemo(
    () =>
      organizations.map((organization) => ({
        organizationId: organization.id,
        organizationName: organization.name || organization.id,
        entitlements: getOrganizationEntitlements(entitlements, organization.id),
      })),
    [entitlements, organizations]
  );
  const selectedPack = useMemo(
    () => contentPacks.find((contentPack) => contentPack.contentPackId === selectedPackId) || null,
    [contentPacks, selectedPackId]
  );

  useEffect(() => {
    if (!selectedPack) return;

    setPackEditForm({
      name: selectedPack.name || '',
      description: selectedPack.description || '',
      industry: selectedPack.industry || '',
      disciplinesText: (selectedPack.disciplines || []).join(', '),
      isPremium: Boolean(selectedPack.isPremium),
    });
  }, [selectedPack]);

  const handleCreatePack = async () => {
    try {
      await createContentPack(db, {
        name: packForm.name,
        description: packForm.description,
        ownerType: 'platform',
        ownerId: 'platform',
        industry: packForm.industry,
        disciplines: packForm.disciplinesText.split(',').map((item) => item.trim()).filter(Boolean),
        isPremium: packForm.isPremium,
      });
      setMessage('Content pack created.');
      setError('');
    } catch (createError) {
      console.error(createError);
      setError('Could not create the content pack.');
    }
  };

  const handleUpdatePackPremium = async (contentPackId, isPremium) => {
    try {
      await updateDoc(doc(db, 'contentPacks', contentPackId), {
        isPremium,
      });
      setContentPacks((current) =>
        current.map((contentPack) =>
          contentPack.contentPackId === contentPackId
            ? { ...contentPack, isPremium }
            : contentPack
        )
      );
      setMessage('Content pack updated.');
      setError('');
    } catch (updateError) {
      console.error(updateError);
      setError('Could not update the content pack.');
    }
  };

  const handleSavePackMetadata = async () => {
    if (!selectedPackId) return;

    try {
      await updateDoc(doc(db, 'contentPacks', selectedPackId), {
        name: packEditForm.name,
        description: packEditForm.description,
        industry: packEditForm.industry,
        disciplines: packEditForm.disciplinesText.split(',').map((item) => item.trim()).filter(Boolean),
        isPremium: packEditForm.isPremium,
      });
      setContentPacks((current) =>
        current.map((contentPack) =>
          contentPack.contentPackId === selectedPackId
            ? {
                ...contentPack,
                name: packEditForm.name,
                description: packEditForm.description,
                industry: packEditForm.industry,
                disciplines: packEditForm.disciplinesText.split(',').map((item) => item.trim()).filter(Boolean),
                isPremium: packEditForm.isPremium,
              }
            : contentPack
        )
      );
      setMessage('Content pack metadata updated.');
      setError('');
    } catch (updateError) {
      console.error(updateError);
      setError('Could not update the content pack metadata.');
    }
  };

  const handleGrantEntitlement = async () => {
    try {
      await grantOrganizationEntitlement(db, {
        organizationId: entitlementForm.organizationId,
        contentPackId: entitlementForm.contentPackId,
        status: 'active',
        actorUserId: userData?.uid || '',
        actorName: userData?.name || '',
      });
      setMessage('Entitlement granted.');
      setError('');
    } catch (grantError) {
      console.error(grantError);
      setError('Could not grant the entitlement.');
    }
  };

  const getEntitlementDraft = (entitlement) => ({
    startDate: entitlementDrafts[entitlement.id]?.startDate ?? formatEntitlementDateInput(entitlement.startDate),
    endDate: entitlementDrafts[entitlement.id]?.endDate ?? formatEntitlementDateInput(entitlement.endDate),
  });

  const handleEntitlementDraftChange = (entitlementId, field, value) => {
    setEntitlementDrafts((current) => ({
      ...current,
      [entitlementId]: {
        ...(current[entitlementId] || {}),
        [field]: value,
      },
    }));
  };

  const handleSaveEntitlementDates = async (entitlement) => {
    try {
      const draft = getEntitlementDraft(entitlement);
      await updateEntitlementDates(db, {
        entitlementId: entitlement.id,
        startDate: draft.startDate || null,
        endDate: draft.endDate || null,
      });
      setEntitlements((current) =>
        current.map((item) =>
          item.id === entitlement.id
            ? {
                ...item,
                startDate: draft.startDate || null,
                endDate: draft.endDate || null,
              }
            : item
        )
      );
      setMessage('Entitlement dates updated.');
      setError('');
    } catch (updateError) {
      console.error(updateError);
      setError('Could not update the entitlement dates.');
    }
  };

  const handleRevokeEntitlement = async (entitlement) => {
    try {
      await revokeOrganizationEntitlement(db, {
        entitlementId: entitlement.id,
        organizationId: entitlement.organizationId,
        contentPackId: entitlement.contentPackId,
        actorUserId: userData?.uid || '',
        actorName: userData?.name || '',
      });
      setEntitlements((current) =>
        current.map((item) =>
          item.id === entitlement.id
            ? { ...item, status: 'revoked' }
            : item
        )
      );
      setMessage('Entitlement revoked.');
      setError('');
    } catch (revokeError) {
      console.error(revokeError);
      setError('Could not revoke the entitlement.');
    }
  };

  if (!editable) {
    return (
      <div className="mx-auto max-w-6xl p-4">
        <ProfileHeader />
        <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Platform</p>
          <h1 className="mt-2 text-2xl font-semibold text-slate-950">Content Packs</h1>
          <p className="mt-3 text-sm text-slate-600">
            This page is only available to platform admins.
          </p>
        </section>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl p-4">
      <ProfileHeader />
      <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Platform</p>
        <h1 className="mt-2 text-2xl font-semibold text-slate-950">Content Pack Management</h1>
        {message ? <p className="mt-4 rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{message}</p> : null}
        {error ? <p className="mt-4 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</p> : null}

        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <section className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
            <h2 className="text-lg font-semibold text-slate-950">Create Content Pack</h2>
            <div className="mt-3 space-y-2">
              <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Name" value={packForm.name} onChange={(e) => setPackForm((current) => ({ ...current, name: e.target.value }))} />
              <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Description" value={packForm.description} onChange={(e) => setPackForm((current) => ({ ...current, description: e.target.value }))} />
              <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Industry" value={packForm.industry} onChange={(e) => setPackForm((current) => ({ ...current, industry: e.target.value }))} />
              <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Disciplines (comma separated)" value={packForm.disciplinesText} onChange={(e) => setPackForm((current) => ({ ...current, disciplinesText: e.target.value }))} />
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={packForm.isPremium} onChange={(e) => setPackForm((current) => ({ ...current, isPremium: e.target.checked }))} />
                Premium pack
              </label>
              <button className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white" onClick={handleCreatePack}>
                Create Pack
              </button>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
            <h2 className="text-lg font-semibold text-slate-950">Grant Entitlement</h2>
            <div className="mt-3 space-y-2">
              <select className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={entitlementForm.contentPackId} onChange={(e) => setEntitlementForm((current) => ({ ...current, contentPackId: e.target.value }))}>
                <option value="">Choose content pack</option>
                {contentPacks.map((contentPack) => (
                  <option key={contentPack.contentPackId} value={contentPack.contentPackId}>{contentPack.name}</option>
                ))}
              </select>
              <select className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={entitlementForm.organizationId} onChange={(e) => setEntitlementForm((current) => ({ ...current, organizationId: e.target.value }))}>
                <option value="">Choose organization</option>
                {organizations.map((organization) => (
                  <option key={organization.id} value={organization.id}>{organization.name || organization.id}</option>
                ))}
              </select>
              <button className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700" onClick={handleGrantEntitlement}>
                Grant Entitlement
              </button>
            </div>
          </section>
        </div>

        <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
          <h2 className="text-lg font-semibold text-slate-950">Content Packs</h2>
          {loading ? (
            <p className="mt-3 text-sm text-slate-500">Loading content packs...</p>
          ) : (
            <div className="mt-3 space-y-3">
              {contentPacks.map((contentPack) => (
                <div key={contentPack.contentPackId} className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-700">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-medium text-slate-900">{contentPack.name}</div>
                      <div>{contentPack.description || 'No description yet.'}</div>
                      <div className="text-xs text-slate-500">
                        Attached drills: {packAttachmentCounts[contentPack.contentPackId] || 0} · Entitlements: {entitlements.filter((entitlement) => entitlement.contentPackId === contentPack.contentPackId).length}
                      </div>
                      <div className="mt-2 text-xs text-slate-500">
                        {templates
                          .filter((template) => template.contentPackId === contentPack.contentPackId)
                          .slice(0, 3)
                          .map((template) => template.title || template.id)
                          .join(', ') || 'No attached drills yet.'}
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        {getContentPackEntitledOrganizations(entitlements, contentPack.contentPackId)
                          .map((entitlement) => organizationNamesById[entitlement.organizationId] || entitlement.organizationId)
                          .join(', ') || 'No entitled organizations yet.'}
                      </div>
                    </div>
                    <button className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700" onClick={() => handleUpdatePackPremium(contentPack.contentPackId, !contentPack.isPremium)}>
                      Mark {contentPack.isPremium ? 'Free' : 'Premium'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="mt-6 grid gap-4 lg:grid-cols-2">
          <section className="rounded-2xl border border-slate-200 bg-white p-5">
            <h2 className="text-lg font-semibold text-slate-950">Entitlement Management</h2>
            <div className="mt-3 space-y-3">
              {entitlements.map((entitlement) => {
                const draft = getEntitlementDraft(entitlement);
                return (
                  <div key={entitlement.id} className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-700">
                    <div className="font-medium text-slate-900">
                      {organizationNamesById[entitlement.organizationId] || entitlement.organizationId}
                    </div>
                    <div className="text-xs text-slate-500">
                      Pack: {contentPacks.find((pack) => pack.contentPackId === entitlement.contentPackId)?.name || entitlement.contentPackId} · Status: {entitlement.status}
                    </div>
                    <div className="mt-3 grid gap-2 md:grid-cols-2">
                      <input
                        className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        placeholder="Start date"
                        value={draft.startDate || ''}
                        onChange={(e) => handleEntitlementDraftChange(entitlement.id, 'startDate', e.target.value)}
                      />
                      <input
                        className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        placeholder="End date"
                        value={draft.endDate || ''}
                        onChange={(e) => handleEntitlementDraftChange(entitlement.id, 'endDate', e.target.value)}
                      />
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700" onClick={() => handleSaveEntitlementDates(entitlement)}>
                        Save Dates
                      </button>
                      {entitlement.status !== 'revoked' ? (
                        <button className="rounded-xl border border-rose-200 px-3 py-2 text-xs font-semibold text-rose-700" onClick={() => handleRevokeEntitlement(entitlement)}>
                          Revoke
                        </button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-5">
            <h2 className="text-lg font-semibold text-slate-950">Organizations By Pack Access</h2>
            <div className="mt-3 space-y-3">
              {organizationEntitlementSummary.map((summary) => (
                <div key={summary.organizationId} className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-700">
                  <div className="font-medium text-slate-900">{summary.organizationName}</div>
                  <div className="text-xs text-slate-500">
                    {summary.entitlements.length} entitlement{summary.entitlements.length === 1 ? '' : 's'}
                  </div>
                  <div className="mt-2 text-xs text-slate-500">
                    {summary.entitlements
                      .map((entitlement) => contentPacks.find((pack) => pack.contentPackId === entitlement.contentPackId)?.name || entitlement.contentPackId)
                      .join(', ') || 'No content packs granted yet.'}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
            <h2 className="text-lg font-semibold text-slate-950">Edit Content Pack</h2>
            <div className="mt-3 space-y-2">
              <select className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={selectedPackId} onChange={(e) => setSelectedPackId(e.target.value)}>
                <option value="">Choose content pack</option>
                {contentPacks.map((contentPack) => (
                  <option key={contentPack.contentPackId} value={contentPack.contentPackId}>{contentPack.name}</option>
                ))}
              </select>
              <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Name" value={packEditForm.name} onChange={(e) => setPackEditForm((current) => ({ ...current, name: e.target.value }))} />
              <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Description" value={packEditForm.description} onChange={(e) => setPackEditForm((current) => ({ ...current, description: e.target.value }))} />
              <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Industry" value={packEditForm.industry} onChange={(e) => setPackEditForm((current) => ({ ...current, industry: e.target.value }))} />
              <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Disciplines (comma separated)" value={packEditForm.disciplinesText} onChange={(e) => setPackEditForm((current) => ({ ...current, disciplinesText: e.target.value }))} />
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={packEditForm.isPremium} onChange={(e) => setPackEditForm((current) => ({ ...current, isPremium: e.target.checked }))} />
                Premium pack
              </label>
              <button className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700" onClick={handleSavePackMetadata}>
                Save Pack Metadata
              </button>
            </div>
          </section>
        </section>
      </section>
    </div>
  );
}

export default PlatformAdminContentPacksPage;
  const formatEntitlementDateInput = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (value?.seconds) {
      return new Date(value.seconds * 1000).toISOString().slice(0, 10);
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
  };
